// Break import sorting rules to import jQuery and its patches together.
/* eslint-disable-next-line import-x/order */
import $ from 'jquery';
import 'lib/jQueryPatches';

import {datadogLogs} from '@datadog/browser-logs';
import {datadogRum} from '@datadog/browser-rum';
import {captureMessage, setUser} from '@sentry/browser';
import Backbone from 'backbone';
import DOMPurify from 'dompurify';
import * as LDClient from 'launchdarkly-js-client-sdk';
import semver from 'semver';
import {UAParser} from 'ua-parser-js';

import * as analytics from 'analytics/Analytics';
import mountCookieConsentManager from 'analytics/mountCookieConsentManager';
import {assert, assertAndContinue} from 'Asserts';
import * as IdleTimeout from 'auth/idle-timeout/IdleTimeout';
import * as PolicyReduxActions from 'auth/PolicyReduxActions';
import BenchlingConfig from 'BenchlingConfig';
import * as benchlingUtil from 'BenchlingUtil';
import {clientHeadersSetter, pageLoadID} from 'ClientHeaders';
import SequenceAttributeCollection from 'components/SequenceAttributeCollection';
import {NAV_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import * as csrf from 'CSRF';
import doServiceWorkerFastRefresh from 'dev/doServiceWorkerFastRefresh';
import * as errorconfig from 'ErrorConfig';
import {getLaunchDarklyContext} from 'launchdarkly/LaunchDarklyUtil';
import {isNodeTesting} from 'lib/Browser';
import localStorage from 'LocalStorage';
import {initMonospaceFontCorrection} from 'molbio/maps/MonospaceFontCorrection';
import {mountAdblockWarning} from 'nav/toasts/AdblockWarning';
// @ts-expect-error
import drafts from 'notebook/store/data-recovery/Drafts';
import notifications, {NotificationOptions} from 'notifications';
import {configureDatadogLogs} from 'observability/DatadogBrowserLogs';
import {configureDatadogRum} from 'performance/RUM';
import {configureSentry} from 'performance/Sentry';
import preferences from 'Preferences';
import {getStore} from 'redux-stores/Store';
import * as shareAccess from 'ShareAccess';
import * as ModalContainer from 'ui-library/ModalContainer';
import * as AdminReduxActions from 'useradmin/AdminReduxActions';
import {warn} from 'util/Logging';
import * as net from 'util/Net';
import {resumePersistedTasks} from 'util/resume-persisted-tasks/ResumePersistedTasks';
import * as TaskRunner from 'util/TaskRunner';
import {setupUser} from 'util/UserUtil';

interface BrowserFamily {
  minimumVersion: string;
  userAgentStrings: Array<string>;
}

const MIN_SUPPORTED_BROWSER_VERSIONS: {[browser: string]: BrowserFamily} = {
  Chrome: {
    minimumVersion: '70.0.0',
    userAgentStrings: ['Chrome', 'Chromium'],
  },
  Edge: {
    minimumVersion: '84.0.0',
    userAgentStrings: ['Edge'],
  },
  Firefox: {
    minimumVersion: '78.0.0',
    userAgentStrings: ['Firefox'],
  },
  Opera: {
    minimumVersion: '83.0.0',
    userAgentStrings: ['Opera'],
  },
  Safari: {
    minimumVersion: '11.1.0',
    userAgentStrings: ['Safari', 'Mobile Safari'],
  },
};

function _dismissBrowserNotice(): void {
  localStorage.json.setItem('hideUnsupportedBrowserNotice', true);
}

function getCoercedBrowserVersion(version?: string): string | null {
  return semver.valid(semver.coerce(version));
}

function getBrowserFamily(name?: string): BrowserFamily | null {
  if (name == null) {
    return null;
  }
  return (
    Object.values(MIN_SUPPORTED_BROWSER_VERSIONS).find(({userAgentStrings}) =>
      userAgentStrings.includes(name)
    ) ?? null
  );
}

export function isBrowserSupported(browser: UAParser.IBrowser): boolean {
  const version = getCoercedBrowserVersion(browser.version);
  const browserFamily = getBrowserFamily(browser.name);
  return browserFamily != null && version != null && semver.gte(version, browserFamily.minimumVersion);
}

class App {
  _setBrowserContext(): void {
    const browser = new UAParser().getBrowser();
    window.isBrowserSupported = isBrowserSupported(browser);

    // Google may index pages with JS running, so we'd like to avoid showing the message.
    window.isGoogleBot = navigator.userAgent.indexOf('Googlebot') >= 0;
  }

  initialize(): void {
    this.initAjaxSetup();

    // can enable SVG and/or MathML output if desired later
    DOMPurify.setConfig({USE_PROFILES: {html: true, svg: true}});

    this._setBrowserContext();

    const sentryDSN = window.isBrowserSupported ? window.SENTRY_DSN_JS : window.SENTRY_DSN_JS_NSB;
    if (sentryDSN) {
      configureSentry(sentryDSN);
    }

    if (
      BenchlingConfig.PERF_ENABLE_REAL_USER_MONITORING &&
      window.DATADOG_RUM_APPLICATION_ID &&
      window.DATADOG_RUM_CLIENT_TOKEN
    ) {
      configureDatadogRum(window.DATADOG_RUM_APPLICATION_ID, window.DATADOG_RUM_CLIENT_TOKEN);
    }

    if (window.DATADOG_LOGS_CLIENT_TOKEN) {
      configureDatadogLogs(window.DATADOG_LOGS_CLIENT_TOKEN);
    }

    analytics.trackEvent(NAV_ANALYTICS_ACTIONS.INITIALIZED_APP, {
      clientWidth: document.documentElement.clientWidth,
      clientHeight: document.documentElement.clientHeight,
    });

    // Don't try to make a LaunchDarkly client if we're in a unit test.
    if (!isNodeTesting) {
      const ldContext = getLaunchDarklyContext();
      // Streaming disabled in order to work around a Safari bug where LD streaming blocks printing.
      // Workaround, if we ever wish to reenable streaming, is to stop streaming immediately before calling
      // window.print and to start streaming again immediately after the call.
      // https://github.com/launchdarkly/js-client-sdk/issues/121

      let ldClient: LDClient.LDClient;
      if (window.LAUNCHDARKLY_CLIENT_URL) {
        ldClient = LDClient.initialize('default', ldContext, {
          baseUrl: window.LAUNCHDARKLY_CLIENT_URL,
          streamUrl: window.LAUNCHDARKLY_CLIENT_URL,
          eventsUrl: window.LAUNCHDARKLY_CLIENT_URL,
        });
      } else {
        ldClient = LDClient.initialize(window.LAUNCHDARKLY_CLIENT_ID, ldContext, {streaming: false});
      }

      // LaunchDarkly clients must be singletons, so make this one global. That way,
      // {@link LaunchDarklyProvider} can grab it and pass it into LD's own context provider so that it does
      // not try to instantiate one client per React root.
      window.ldClient = {...ldClient, ready: false};
      // Track ready state on the client object itself to avoid unnecessary remounts in modals
      ldClient.on('initialized', () => {
        ldClient.track(window.LAUNCHDARKLY_CLIENT_ID);
      });
    }
  }

  initAjaxSetup(): void {
    $.ajaxSetup({
      beforeSend(xhr: JQueryXHR, settings: JQueryAjaxSettings): void {
        csrf.csrfSetter.call(this, xhr, settings);
        clientHeadersSetter.call(this, xhr, settings);
        shareAccess.shareAccessSetter.call(this, xhr, settings);
        SequenceAttributeCollection.sequenceVersionSetter.call(this, xhr, settings);
        errorconfig.fsURLSetter.call(this, xhr, settings);
      },
      complete(xhr: JQueryXHR, textStatus: string): void {
        // Display warnings in a toast if present.
        net.getResponseWarnings(xhr).forEach((warning) => {
          notifications.warn(warning, {duration: 0});
        });
      },
    });
  }

  run(): void {
    const browser = new UAParser().getBrowser();

    // oxlint-disable-next-line no-console
    console.log(`Running app,
      pageLoadID=${pageLoadID},
      buildDate=${window.BUILD_TIMESTAMP?.toISOString() || '<development>'},
      clientAPIVersion=${window.clientAPIVersion},
      revision=${window.REVISION},
      browser=${browser.name}@${browser.version}`);
    if (window.location.hash === '#__SIMULATE_JS_LOAD_FAILURE__') {
      throw new Error('Simulating unexpected JS crash');
    }
    notifications.init();
    if (
      !window.isBrowserSupported &&
      !localStorage.json.getItem('hideUnsupportedBrowserNotice') &&
      !window.SUPPRESS_USERAGENT_ERROR &&
      !window.isGoogleBot
    ) {
      const kbURL = '/browsers';
      notifications.error(
        `Benchling is actively tested against the latest versions of Chrome,
        Firefox, Safari, and Edge. **It doesn't look like your current browser is supported** -
        for more information, [click here](${kbURL}).`,
        {duration: 0, onClose: _dismissBrowserNotice}
      );
    }
    ModalContainer.init();
    drafts.init();
    preferences.init();

    this._bindGlobalListeners();

    if (window.bootstrapUser != null) {
      setupUser(window.bootstrapUser);
    }
    if (window.bootstrap && window.bootstrap.adminStoreState) {
      getStore().dispatch(AdminReduxActions.adminStateInitialized(window.bootstrap.adminStoreState));
    }
    if (window.bootstrap && window.bootstrap.dataPolicies) {
      getStore().dispatch(PolicyReduxActions.policiesBootstrapped(window.bootstrap.dataPolicies));
    }
    net.xhrCounter.start();

    // Poll for client API version
    function checkClientAPIVersion(): void {
      net
        .postJSON({
          untracked: true,
          url: '/1/api/_poll',
          data: {
            client_api_version: window.clientAPIVersion,
            url: window.location.href,
          },
        })
        .done((res) => {
          if (res.message != null) {
            const options: NotificationOptions = {
              duration: 0,
              id: 'client-api-message',
            };
            if (res.closeable != null) {
              options.closeable = res.closeable;
            }
            notifications.info(res.message, options);
          }
          setTimeout(checkClientAPIVersion, res.timeout);
        })
        .fail((res) => {
          setTimeout(checkClientAPIVersion, 10 * 1000);
        });
    }

    checkClientAPIVersion();

    mountCookieConsentManager();
    mountAdblockWarning();
    initMonospaceFontCorrection();

    // Sentry / Datadog
    if (!window.debug && window.user != null) {
      const userData: {id: string; email: string; impersonating?: boolean} = {
        email: window.user.email,
        id: window.user.url_identifier,
      };
      if (window.impersonating) {
        userData.impersonating = true;
      }
      setUser(userData);
      datadogRum.setUser(userData);
      datadogLogs.setUser(userData);
    }

    resumePersistedTasks();
  }

  runWithBackbone(): void {
    this.run();
    Backbone.history.start({pushState: true});
  }

  hotReload(): void {
    // Routers are re-initialized on hot reload, so we need to restart history to prevent duplication.
    Backbone.history.stop();
    Backbone.history.start({pushState: true});
    Backbone.history.loadUrl();
  }

  _bindGlobalListeners(): void {
    // Allow setting data-method on a link to have clicking it submit a non-GET request.
    // Currently only POST is supported.
    $(document).on(
      'click.aurelia',
      'a[data-method]',
      function clickHandler(this: HTMLLinkElement, e: JQueryMouseEventObject): void {
        // Don't handle if someone else has handled.
        if (e.isDefaultPrevented()) {
          return;
        }
        const $link = $(this);
        const method = $link.data('method');
        e.preventDefault();
        if (method !== 'POST') {
          throw new Error(`Method ${method} not supported`);
        }
        net.submitCSRFForm(method, $link.attr('href'));
      }
    );

    IdleTimeout.initListeners();

    $(window).on('beforeunload', () => {
      window.beforeUnloadType = 'default';
    });

    $(document).on('click.aurelia', '.toasts-mount a[href="mailto:support@benchling.com"]', (e) => {
      e.preventDefault();
      benchlingUtil.contactBenchlingViaEmail();
    });

    errorconfig.addListeners();
    errorconfig.installErrorHandler();
    errorconfig.installUnhandledRejectionHandler();

    if (window.debug) {
      $(document).keyup((e) => {
        if (e.ctrlKey && e.shiftKey && e.key === '1') {
          throw new Error('You pressed Ctrl + Shift + 1. How dare you?');
        }
        if (e.ctrlKey && e.shiftKey && e.key === 'R') {
          doServiceWorkerFastRefresh();
        }
      });
    }

    if (window.location.hash === '#force-error') {
      // Should get undefined reference.
      $(document).on('click', () => {
        const a = (): void => {
          // @ts-expect-error: Intentional unused variable to cause error.
          console.log(forcedError); // oxlint-disable-line no-console
        };

        function b(): void {
          console.log('going...'); // oxlint-disable-line no-console
          a();
        }

        function c(): void {
          console.log('another stack layer'); // oxlint-disable-line no-console
          b();
        }

        c();
      });
    } else if (window.location.hash === '#_ajax-error') {
      // Should get undefined reference.
      $.get('/').done(() => {
        const a = (): void => {
          // @ts-expect-error: Intentional unused variable to cause error.
          console.log(forcedAjaxError); // oxlint-disable-line no-console
        };

        function b(): void {
          console.log('going...'); // oxlint-disable-line no-console
          a();
        }

        function c(): void {
          console.log('another stack layer'); // oxlint-disable-line no-console
          b();
        }

        c();
      });
    } else if (window.location.hash === '#_promise-error') {
      // Should get undefined reference.
      $(document).on('click', () => {
        const a = (): Promise<never> => Promise.reject(new Error('Test promise rejection'));

        async function b(): Promise<void> {
          await a();
        }

        async function c(): Promise<void> {
          await b();
        }

        c();
      });
    } else if (window.location.hash === '#_task-polling-timeout') {
      $(document).on('click', () => {
        function longTaskTimeout(): void {
          TaskRunner.runTask(
            {
              url: '/__debug__/run-sleep-task',
              type: 'GET',
              contentType: 'application/json',
              data: JSON.stringify({seconds: 15}),
            },
            5 * 1000
          )
            .done(() => {
              console.log('No timeout occurred in task-polling-timeout'); // oxlint-disable-line no-console
            })
            .fail((res) => {
              console.log(res); // oxlint-disable-line no-console
              notifications.notifyError(res);
            });
        }
        console.log('task polling for timeout started'); // oxlint-disable-line no-console
        longTaskTimeout();
      });
    } else if (window.location.hash === '#_request_timeout') {
      $(document).on('click', () => {
        net
          .getJSONPromise('/__debug__/sleep-with-time-limit', {seconds: 10})
          .then(() => {
            console.log('No timeout occurred in task-polling-timeout'); // oxlint-disable-line no-console
          })
          .catch((res) => {
            console.log(res); // oxlint-disable-line no-console
            notifications.notifyError(res);
          });
      });
    } else if (window.location.hash === '#_asserts') {
      $(document).on('click', () => {
        warn('Test warning logs', {tags: {component: 'PERFORMANCE'}, extra: {dataAction: 'log.warn'}});
        captureMessage('Message captured', {
          tags: {component: 'PERFORMANCE'},
          extra: {dataAction: 'captureMessage'},
        });
        assertAndContinue(
          false,
          'Test assertAndContinue failed 1==0 test',
          {dataAction: 'assert&continue'},
          {tags: {component: 'PERFORMANCE'}}
        );
        assert(
          false,
          'Test assert failed 1==0 test',
          {dataAction: 'assert'},
          {tags: {component: 'PERFORMANCE'}}
        );
      });
    }
  }
}

export default App;
